import React, { useEffect, useMemo, useState, useRef } from 'react';
import CardCommon, { CardCommonType } from '../CardCommon';
import { copyIcon, editIcon, fullIcon, IconType } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import RichBodyBox from './RichBodyBox';
import RichTextShowBox from './RichTextShowBox';
import RichTextEditModal, {
  SubmitClickType,
  SubmitValueType,
} from './RichTextEditModal';
import useFullScreen from '@op/hooks/useFullScreen';
import { SummaryTabItem } from './types';
import { useListRefs, useStateRef } from '@op/hooks/common';
import { DBO_T } from '@op/utils/format';
import { useUpdateEffect } from 'ahooks';
import { NoDataCardProps } from '../NoDataCommon';

type RichTextBlockProps = {
  isFull?: boolean;
  tabs: Array<SummaryTabItem>;
  tabsOptionStyle?: React.CSSProperties;
  onConfirm: SubmitClickType;
  bodyHeight?: number; // 展示区的高度
  singleModel?: boolean; // 单个模式，将操作栏显示在标题左侧
  onlyModalTitle?: boolean; // 只有浮窗有标题
  onlyOneLanguage?: boolean;
  emptyProps?: NoDataCardProps;
  canEdit?: boolean; // 是否可以编辑
  canEmptyCommit?: boolean; // 是否可以在内容为空的情况下提交内容
} & CardCommonType;

/**
 * 富文本组件
 * 编辑功能，复制功能，放大功能
 * 如果无数据不展示编辑功能的话,可以传入tabs=[]
 * 如果无数据可以编辑,那么就直接传入tabs
 *
 * @param isFull 是否全部展开，默认是
 * @param tabs  富文本tabs数据
 * @param tabsOptionStyle 浮窗tab的样式
 * @param onConfirm 提交的时候的回调
 * @param bodyHeight 展示区的高度
 * @param singleModel 单个模式，将操作栏显示在标题左侧
 * @param onlyModalTitle 只有浮窗有标题
 * @param onlyOneLanguage 仅展示一种语言
 * @param emptyProps 无数据的样式
 * @param canEdit 是否可以编辑
 * @param canEmptyCommit 是否可以在内容为空的情况下提交内容
 * @returns
 */
export default function RichTextBlock({
  title = '',
  isFull = true,
  tabs,
  tabsOptionStyle,
  onConfirm,
  bodyHeight,
  singleModel = false,
  onlyModalTitle = false,
  onlyOneLanguage = false, 
  canEdit = false,
  emptyProps,
  canEmptyCommit = true,
  ...props
}: RichTextBlockProps) {
  const [tabsList, setTabsList] = useState(tabs || []);
  const [activeTabRef, activeTab, setActiveTab] = useStateRef(tabs?.[0]?.value);
  const { setRef, getRef, initRefs } = useListRefs<HTMLDivElement>(
    tabs?.length || 0,
  );

  useUpdateEffect(() => {
    if (tabs) {
      setTabsList(tabs);
      setActiveTab(tabs?.[0]?.value);
      initRefs(tabs?.length);
    }
  }, [tabs]);

  const [visible, setVisible] = useState(false);
  const { ScreenModal, setVisible: setFullVisible } = useFullScreen();
  const { isEn, formatMessage } = useLanguage();

  const { options, hasTabs } = useMemo(() => {
    return { hasTabs: tabsList.length > 1, options: tabsList };
  }, [tabsList]);

  const isEmpty = useMemo(() => {
    const activeTabItem = options.find((item) => item.value === activeTab);

    const key: 'summary' | 'summaryCn' = isEn ? 'summary' : 'summaryCn';
    return !activeTabItem?.[key];
  }, [activeTab, options, isEn]);

  const { cardOperations, boxOperations } = useMemo(() => {
    let list = [{ ...editIcon, tip: 'Edit' }, copyIcon];
    if (!isFull) {
      list.push(fullIcon);
    }

    if (isEmpty) {
      list = list.filter((item) => item.symbol === 'edit');
    }

    if (!canEdit) {
      list = list.filter((item) => item.symbol !== 'edit');
    }

    const flag = hasTabs || singleModel;
    return {
      cardOperations: flag ? list : [],
      boxOperations: flag ? [] : list,
    };
  }, [hasTabs, isEmpty, canEdit]);

  function handOperations(item: IconType) {
    if (item.symbol === 'copy') {
      const index = tabsList.findIndex(
        (item) => item.value === activeTabRef.current,
      );
      const summaryHtmlRef = getRef(index);
      DBO_T.copy(summaryHtmlRef, formatMessage);
    }

    if (item.symbol === 'fullscreen') {
      setFullVisible(true);
    }

    if (item.symbol === 'edit') {
      setVisible(true);
      setFullVisible(false);
    }
  }

  function successCallback(data: SubmitValueType) {
    const newTabs = tabsList.map((item) => {
      const { value } = item;
      const newItem = data.find((it) => String(it.key) === String(value));

      if (!newItem) return item;

      if (onlyOneLanguage) {
        return { ...item, summary: newItem.text, summaryCn: newItem.text };
      }

      return { ...item, [isEn ? 'summary' : 'summaryCn']: newItem.text };
    });

    setTabsList(newTabs);
  }

  return (
    <>
      <CardCommon
        className="dbo-rich-text-block-wrapper"
        title={onlyModalTitle ? undefined : title}
        operations={cardOperations}
        alwaysShowOperations
        operationInfo={['', () => {}]}
        empty={!tabsList || tabsList.length === 0}
        handleOperationsOnClick={(item) => handOperations(item as IconType)}
        {...props}
      >
        <RichBody
          tabsOptionStyle={tabsOptionStyle}
          hasTabs={hasTabs}
          activeTab={activeTab!}
          setActiveTab={setActiveTab}
          options={options}
          isFull={isFull}
          setRef={setRef as any}
          operations={boxOperations}
          canEdit={canEdit}
          handOperations={handOperations}
          bodyHeight={bodyHeight}
          emptyProps={emptyProps}
        ></RichBody>
      </CardCommon>

      <RichTextEditModal
        visible={visible}
        title={title}
        hasTabs={hasTabs}
        defaultActiveTab={activeTab!}
        options={options}
        canEmptyCommit={canEmptyCommit}
        close={() => setVisible(false)}
        submit={onConfirm}
        successCallback={successCallback}
      ></RichTextEditModal>

      <ScreenModal title={title} hasRightIcon={false}>
        <RichBody
          tabsOptionStyle={tabsOptionStyle}
          hasTabs={hasTabs}
          activeTab={activeTabRef.current!}
          options={options}
          bodyHeight={600}
          isFull={false}
          canEdit={canEdit}
          handOperations={handOperations}
        ></RichBody>
      </ScreenModal>
    </>
  );
}

function RichBody({
  options,
  activeTab,
  setActiveTab,
  hasTabs,
  bodyHeight,
  isFull,
  handOperations,
  operations,
  setRef,
  emptyProps,
  tabsOptionStyle,
  canEdit,
}: {
  options: Array<SummaryTabItem>;
  activeTab: string;
  setActiveTab?: (value: string) => void;
  hasTabs: boolean;
  bodyHeight?: number;
  isFull: boolean;
  handOperations?: (item: IconType) => void;
  operations?: Array<IconType>;
  setRef?: (index: number) => (value: HTMLDivElement) => void;
  emptyProps?: NoDataCardProps;
  tabsOptionStyle?: React.CSSProperties;
  canEdit: boolean;
}) {
  const [localActiveTab, setLocalActiveTab] = useState(activeTab);
  const summaryRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (localActiveTab !== activeTab) {
      setLocalActiveTab(activeTab);
    }
  }, [activeTab]);

  function changeActiveTab(value: string) {
    setLocalActiveTab(value);
    setActiveTab?.(value);
  }

  return (
    <RichBodyBox
      hasTabs={hasTabs}
      activeTab={localActiveTab}
      setActiveTab={changeActiveTab}
      options={options}
      tabsOptionStyle={tabsOptionStyle}
    >
      {(item, index) => {
        return (
          <RichTextShowBox
            item={item}
            canEdit={canEdit}
            emptyProps={emptyProps}
            bodyHeight={bodyHeight}
            isFull={isFull}
            operations={operations}
            setRef={(value: HTMLDivElement) => {
              if (setRef) {
                setRef?.(index)?.(value);
              }
              summaryRef.current = value;
            }}
            handOperations={handOperations}
          ></RichTextShowBox>
        );
      }}
    </RichBodyBox>
  );
}
